import { App, Button, Dropdown, Row, Spin, Table, type MenuProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import { MdFileDownload } from "react-icons/md";
import ActionButton from "../../masterComponents/actionButton";
import Icon from "../../masterComponents/Icon";
import { ALL_BATCHES, FileIconMap } from "../../../entities/entities";
import { getFileExtension } from "../../../utils";
import { useFetchData as usePagination } from "../../../hooks/useFetch/pagination";
import { useFetchData as useGetData } from "../../../hooks/useFetch/get";
import { ContainerWrapper, SpinWrapper } from "../../../app/app.style";
import { FaChevronDown } from "react-icons/fa";
import type { CommonDropdownType } from "./ReportsType";
import { LoadingOutlined } from "@ant-design/icons";
import { buildFilterStructure } from "../../../utils/filtersStructureHelpers";

const columns = [
  {
    title: "Name",
    dataIndex: "name",
  },
  {
    title: "Type",
    dataIndex: "type",
  },
  {
    title: "Load",
    dataIndex: "load",
  },
];

const PAGE_SIZE = 25;

const ALL_KEY = "All";

const SOURCE_ITEMS = [
  {
    label: "Unloading",
    key: "unloading",
  },
  {
    label: "Rules",
    key: "rules",
  },
];

const CommonDropdown: React.FC<CommonDropdownType> = ({
  value,
  options,
  onChange,
}) => {
  const items: MenuProps["items"] = [{ label: "All", key: "all" }, ...options];

  const currentLabel = items.find((item) => item.key === value)?.label || value;

  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items,
        selectedKeys: [value],
        onClick: ({ key }) => onChange(key),
      }}
    >
      <Button
        icon={<Icon icon={FaChevronDown} />}
        className="btn-color"
        style={{
          marginRight: "var(--spacing-xs)",
          justifyContent: "flex-start",
          width: 250,
        }}
      >
        {currentLabel}
      </Button>
    </Dropdown>
  );
};

const Reports = () => {
  const [batch, setBatch] = useState(ALL_KEY);
  const [source, setSource] = useState(ALL_KEY);

  const { message: messageApi } = App.useApp();

  const filterStructure = buildFilterStructure(
    ["rules", "unloading"].includes(source) ? [{ key: "type", values: [source] }] : []
  );

  const [
    fetchReportsData,
    {
      isLoading,
      data: reportsData,
      page,
      totalRows,
      error,
      loadMore,
      clearError,
    },
  ] = usePagination({
    query: "/reports",
    perPage: PAGE_SIZE,
    selectData: (data) => data.items,
    selectPageInfo: (data) => data.page_info,
  });

  const [
    fetchReportUrl,
    { data: reportUrl, error: reportUrlError, clearError: clearReportUrl },
  ] = useGetData({
    query: "/reports/@_id/download",
    selectData: (data) => data,
  });

  useEffect(() => {
    fetchReportsData({}, { filters: filterStructure });
  }, [source]);

  useEffect(() => {
    if (error) {
      messageApi.error(error.message);
      clearError();
    }
  }, [error, messageApi]);

  const reportsDataTable = useMemo(
    () =>
      reportsData?.map((report) => {
        const IconAndName = FileIconMap[getFileExtension(report.file_name)];
        return {
          id: report._id,
          name: report.file_name,
          type: (
            <div
              style={{
                display: "flex",
                gap: "4px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Icon
                icon={IconAndName.icon}
                size={24}
                color={IconAndName.color}
              />
              <span>{IconAndName.name}</span>
            </div>
          ),
          load:
            report.status == "SUCCESS" ? (
              <ActionButton
                className="btn-color"
                styles={{
                  padding: "var(--spacing-sm)",
                  dsplay: "flex",
                  justifyContent: "center",
                }}
                onClick={() => fetchReportUrl({ _id: report._id })}
                icon={<Icon icon={MdFileDownload} size={24} />}
              />
            ) : (
              <span>{report.status}</span>
            ),
        };
      }),
    [reportsData],
  );

  useEffect(() => {
    if (reportUrl && reportUrl.download_url) {
      window.location.href = reportUrl.download_url;
    }
  }, [reportUrl]);

  useEffect(() => {
    if (reportUrlError) {
      messageApi.error(reportUrlError.message);
      clearReportUrl();
    }
  }, [reportUrlError, messageApi]);

  const filters = {
    batch: batch === ALL_KEY ? ALL_BATCHES.map((o) => o.key) : [batch],
    source: source === ALL_KEY ? SOURCE_ITEMS.map((o) => o.key) : [source],
  };

  return (
    <ContainerWrapper $withoutGap="false">
      <Row style={{ justifyContent: "space-between" }}>
        <div>
          <CommonDropdown
            value={batch}
            options={ALL_BATCHES}
            onChange={setBatch}
          />
          <CommonDropdown
            value={source}
            options={SOURCE_ITEMS}
            onChange={setSource}
          />
        </div>

        <SpinWrapper>
          <Spin indicator={<LoadingOutlined />} spinning={isLoading} />
        </SpinWrapper>
      </Row>

      <Table
        className="primary-table"
        rowKey="id"
        showSorterTooltip={false}
        tableLayout="auto"
        columns={columns}
        dataSource={reportsDataTable}
        onChange={(pagination) => {
          loadMore(pagination.current);
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: totalRows,
          showSizeChanger: false,
          showQuickJumper: true,
        }}
      />
    </ContainerWrapper>
  );
};

export default Reports;
